import React, { useState, useEffect } from "react";
import axios from "axios";
import "./StudentCardLong.css";

export default function StudentBar({
  student,
  onView,
  onToggleSave,
  onContact,
}) {
  const [badges, setBadges] = useState([]);
  const [loadingBadges, setLoadingBadges] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const userData = JSON.parse(localStorage.getItem("user")) || {};
  const userId = userData.id || userData._id;

  const handleResumeClick = (e) => {
    e.stopPropagation();
    if (!student.resumeKey) {
      return alert("No CV available");
    }
    axios
      .get(`${process.env.REACT_APP_FASTAPI_URL}/get-resume-link`, {
        params: { file_key: student.resumeKey },
      })
      .then((res) => {
        window.open(res.data.url, "_blank");
      })
      .catch((err) => {
        console.error(err);
        alert("Failed to fetch CV");
      });
  };

  // Fetch student badges
  useEffect(() => {
    (async () => {
      setLoadingBadges(true);
      try {
        if (!student._id) throw new Error();
        const resp = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/badges/user/${student._id}`
        );
        const all = await resp.json();
        const selIds = student.selectedBadges?.map((b) => b._id) || [];
        setBadges(
          all.filter((b) => b.badgeId?._id && selIds.includes(b.badgeId._id))
        );
      } catch {
        setBadges([]);
      } finally {
        setLoadingBadges(false);
      }
    })();
  }, [student._id]);

  // Fetch saved state
  useEffect(() => {
    if (!userId || !student._id) return;
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/saved/${userId}`)
      .then((res) => {
        const savedIds = res.data.savedItems.map((i) => i._id);
        setIsSaved(savedIds.includes(student._id));
      })
      .catch(() => {});
  }, [student._id, userId]);

  // Toggle save/unsave
  const handleHeartClick = (e) => {
    e.stopPropagation();
    if (!userId || !student._id) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/saved/${userId}/${student._id}`;
    const req = isSaved ? axios.delete(url) : axios.post(url);
    req
      .then(() => {
        const now = !isSaved;
        setIsSaved(now);
        if (!now && onToggleSave) onToggleSave(student._id);
      })
      .catch((err) => console.error(err));
  };

  const getBadgeIcon = (icon) => {
    switch (icon) {
      case "star":
        return "⭐";
      case "trophy":
        return "🏆";
      case "medal":
        return "🏅";
      case "fire":
        return "🔥";
      case "heart":
        return "❤️";
      case "crown":
        return "👑";
      case "gem":
        return "💎";
      case "rocket":
        return "🚀";
      default:
        return "🏅";
    }
  };

  // Helper functions to get institution and department display with levels
  const getInstitutionDisplay = () => {
    if (!student.education || student.education.length === 0) {
      return student.institution || "Institution";
    }
    if (student.education.length === 1) {
      const edu = student.education[0];
      const levelLabel = edu.level === "masters" ? "MS" : "BS";
      return `${edu.institution} (${levelLabel})`;
    }
    return student.education.map((edu) => {
      const levelLabel = edu.level === "masters" ? "MS" : "BS";
      return `${edu.institution} (${levelLabel})`;
    });
  };

  const getDepartmentDisplay = () => {
    if (!student.education || student.education.length === 0) {
      return student.department || "N/A";
    }
    if (student.education.length === 1) {
      const edu = student.education[0];
      const levelLabel = edu.level === "masters" ? "MS" : "BS";
      return `${edu.department} (${levelLabel})`;
    }
    return student.education.map((edu) => {
      const levelLabel = edu.level === "masters" ? "MS" : "BS";
      return `${edu.department} (${levelLabel})`;
    });
  };

  const greScores =
    student.greVerbal || student.greQuantitative || student.greWriting
      ? [
          { label: "V", value: student.greVerbal || "N/A" },
          { label: "Q", value: student.greQuantitative || "N/A" },
          { label: "W", value: student.greWriting || "N/A" },
        ]
      : null;

  const interests =
    student.interests?.slice(0, 3).join(", ") +
      (student.interests?.length > 3 ? "..." : "") || "N/A";
  const institutionDisplay = getInstitutionDisplay();
  const departmentDisplay = getDepartmentDisplay();

  return (
    <div className="student-bar-container">
      <div onClick={onView} className="student-bar">
        {/* Heart Icon */}
        <button
          onClick={handleHeartClick}
          className="heart-button"
          aria-label={isSaved ? "Unsave student" : "Save student"}
        >
          {isSaved ? (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              className="heart-filled"
            >
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          ) : (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              className="heart-empty"
            >
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          )}
        </button>

        {/* Student Info */}
        <div className="student-info">
          <h3 className="student-name">{student.name}</h3>
          <div className="institution-info">
            {Array.isArray(institutionDisplay) ? (
              institutionDisplay.map((institution, index) => (
                <div key={index} className="institution-item">
                  {institution}
                </div>
              ))
            ) : (
              <div className="institution-item">{institutionDisplay}</div>
            )}
          </div>
        </div>

        {/* Department */}
        <div className="department-info">
          <div className="section-title">Department</div>
          <div className="department-content">
            {Array.isArray(departmentDisplay) ? (
              departmentDisplay.map((department, index) => (
                <div key={index} className="department-item">
                  {department}
                </div>
              ))
            ) : (
              <div className="department-item">{departmentDisplay}</div>
            )}
          </div>
        </div>

        {/* GPA */}
        <div className="gpa-info">
          <div className="section-title">GPA</div>
          <div className="gpa-value">
            {(() => {
              if (!student.education || student.education.length === 0)
                return "N/A";
              const mostRecentEntry =
                student.education[student.education.length - 1];
              if (!mostRecentEntry || !mostRecentEntry.gpa) return "N/A";
              const formatGpa = (gpa) => {
                return parseFloat(gpa).toString();
              };
              const formattedGpa = formatGpa(mostRecentEntry.gpa);
              const scale = mostRecentEntry.gpaScale || "4.0";
              return `${formattedGpa}/${scale}`;
            })()}
          </div>
        </div>

        {/* GRE */}
        <div className="gre-info">
          <div className="section-title">GRE</div>
          <div className="gre-content">
            {greScores ? (
              greScores.map((s) => (
                <div key={s.label} className="gre-score">
                  <span className="gre-label">{s.label}:</span> {s.value}
                </div>
              ))
            ) : (
              <div className="gre-na">N/A</div>
            )}
          </div>
        </div>

        {/* Research Interests */}
        <div className="interests-info">
          <div className="section-title">Research Interests</div>
          <div className="interests-content">
            <div className="interests-text">{interests}</div>
          </div>
        </div>

        {/* Badges */}
        <div className="badges-info">
          <div className="section-title">Badges</div>
          <div className="badges-content">
            {loadingBadges ? (
              <div className="badges-loading">Loading...</div>
            ) : badges.length ? (
              <div className="badges-list">
                {badges.slice(0, 2).map((b) => (
                  <div
                    key={b._id}
                    className="badge-item"
                    style={{
                      backgroundColor: `${b.badgeId?.color || "#ccc"}15`,
                      borderColor: `${b.badgeId?.color || "#ccc"}40`,
                    }}
                    title={`${b.badgeId?.name}: ${b.badgeId?.description}`}
                  >
                    <span className="badge-icon">
                      {getBadgeIcon(b.badgeId?.icon)}
                    </span>
                    <span
                      className="badge-name"
                      style={{ color: b.badgeId?.color || "#666" }}
                    >
                      {b.badgeId?.name}
                    </span>
                  </div>
                ))}
                {badges.length > 2 && (
                  <div className="badges-more">+{badges.length - 2}</div>
                )}
              </div>
            ) : (
              <div className="no-badges">None</div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (onContact) {
                onContact();
              }
            }}
            className="contact-button"
          >
            Contact
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleResumeClick(e);
            }}
            disabled={!student.resumeKey}
            className="cv-button"
          >
            C.V.
          </button>
        </div>
      </div>
    </div>
  );
}
